import React from "react"

import {
	Box,
	Grid,
} from "@mui/material"

import {
	t,
	Trans
} from "@lingui/macro"
import { UseFormReturn } from "react-hook-form"

import {
	MatesSelect, MatesTextField,
	withUseFormHook
} from "@sinossi/mates-react-library"

import { AdvancedSearchbar } from "components/advanced-searchbar/advanced-searchbar"

import {
	AssetFilter,
	AssetMatchesBrandStatus,
	PositionType,
	PriorityLevel,
	PriorityLevelTranslations,
	CompatibilityTranslations,
} from "models"
import { AssetMatchesBrandStatusTrans } from "models/asset-match-brand"

import MatchProposalAdvancedSearchValidation, { MatchProposalAdvancedSearchForm } from "components/match-proposal/searchbar/form"
import {
	MatchProposalFilter,
} from "components/match-proposal/model"
import { AssetListOrigin } from "components/dashboard/dashboard-assets-list/model"
import withUseRouter, { UseRouterParams, parseQueryParams } from "services/router"

/**
 * Componente per la ricerca avanzata delle proposte di match.
 *
 * Supporta la prepopolazione dei filtri tramite query parameters nell'URL:
 * - brand: nome del brand (stringa)
 * - priority: livello di priorità (HIGH, MEDIUM, LOW)
 * - status: stato della proposta (PENDING, ACCEPTED, REJECTED, etc.)
 * - compatibility: livello di compatibilità (numero)
 * - address: località (stringa)
 * - operator: operatore (stringa, attualmente disabilitato)
 *
 * Esempio di URL:
 * /match-proposal?priority=HIGH&status=PENDING&brand=Nike&address=Milano
 *
 * I filtri vengono automaticamente sincronizzati con l'URL quando vengono modificati.
 */


interface MatchProposalAdvancedSearchProps extends UseFormReturn, UseRouterParams {
	handleCallback: (data: MatchProposalFilter) => void
}

interface MatchProposalAdvancedSearchState {
	loading: boolean
}

class MatchProposalAdvancedSearch extends React.Component<MatchProposalAdvancedSearchProps, MatchProposalAdvancedSearchState> {

	constructor(props: MatchProposalAdvancedSearchProps) {
		super(props);
		this.state = {
			loading: true
		}
	}


	componentDidMount() {
		this.initializeForm()

		this.setState(() => ({
			loading: false
		}))
	}

	private initializeForm = () => {
		// Prima priorità: query parameters dall'URL
		const queryParams = parseQueryParams(this.props.location.search)
		
		// Seconda priorità: localStorage
		const storageRequest: AssetFilter = localStorage.getItem("match-proposal-filter") ? JSON.parse(localStorage.getItem("match-proposal-filter")!) : null

		// Converte i query parameters nel formato del form
		const formDataFromQuery = this.convertQueryParamsToFormData(queryParams)
		
		if (Object.keys(formDataFromQuery).length > 0) {
			// Se ci sono query parameters, usali per inizializzare il form
			this.props.reset(formDataFromQuery)
		} else if (storageRequest) {
			// Altrimenti usa localStorage se disponibile
			this.props.reset(storageRequest)
		} else {
			// Infine, reset al valore di default
			this.props.reset()
		}
	}

	/**
	 * Converte i query parameters nel formato richiesto dal form
	 */
	private convertQueryParamsToFormData = (queryParams: Record<string, string>): Partial<MatchProposalAdvancedSearchForm> => {
		const formData: Partial<MatchProposalAdvancedSearchForm> = {}

		// Mappa i query parameters ai campi del form
		if (queryParams.brand) {
			formData.brand = queryParams.brand
		}
		
		if (queryParams.priority && Object.values(PriorityLevel).includes(queryParams.priority as PriorityLevel)) {
			formData.priority = queryParams.priority as PriorityLevel
		}
		
		if (queryParams.status && Object.values(AssetMatchesBrandStatus).includes(queryParams.status as AssetMatchesBrandStatus)) {
			formData.status = queryParams.status as AssetMatchesBrandStatus
		}
		
		if (queryParams.compatibility) {
			const compatibilityValue = parseInt(queryParams.compatibility, 10)
			if (!isNaN(compatibilityValue)) {
				formData.compatibility = compatibilityValue
			}
		}
		
		if (queryParams.address) {
			formData.address = queryParams.address
		}
		
		
		if (queryParams.operator) {
			formData.operator = queryParams.operator
		}

		return formData
	}

	private onSearch = (data: Record<string, any>): void => {
		const formData: MatchProposalAdvancedSearchForm = new MatchProposalAdvancedSearchForm()
		Object.assign(formData, data)

		const request: MatchProposalFilter = formData.toRequest()
		
		// Aggiorna l'URL con i nuovi parametri di ricerca
		this.updateUrlWithFilters(request)
		
		// Chiama il callback per aggiornare i risultati
		this.props.handleCallback(request)
		
		// Salva anche in localStorage per compatibilità
		localStorage.setItem("match-proposal-filter", JSON.stringify(request))
	}

	/**
	 * Aggiorna l'URL con i parametri di filtro correnti
	 */
	private updateUrlWithFilters = (filter: MatchProposalFilter): void => {
		const queryParams: Record<string, any> = {}

		// Mappa solo i valori non nulli/vuoti ai query parameters
		if (filter.brand) queryParams.brand = filter.brand
		if (filter.priority) queryParams.priority = filter.priority
		if (filter.status) queryParams.status = filter.status
		if (filter.compatibility !== null && filter.compatibility !== undefined) {
			queryParams.compatibility = filter.compatibility.toString()
		}
		if (filter.address) queryParams.address = filter.address
		if (filter.operator) queryParams.operator = filter.operator

		// Costruisce la query string
		const searchParams = new URLSearchParams()
		Object.entries(queryParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== '') {
				searchParams.set(key, String(value))
			}
		})

		// Aggiorna l'URL senza ricaricare la pagina
		const newUrl = `${this.props.location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`
		this.props.navigate(newUrl, { replace: true })
	}

	render() {
		return (
			<Box>
				{
					!this.state.loading &&
					<AdvancedSearchbar
						{...this.props}
						searchFieldName="brand"
						searchFieldPlaceholder="Ricerca proposte"
						onSearch={this.onSearch}
						clearFormButton
					>
						<Grid container spacing={3}>
							{/* Sezione Principale - Brand e Località */}
							<Grid item xs={12}>
								<Box sx={{ mb: 2 }}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<MatesTextField
												name="brand"
												label="Brand"
												variant="outlined"
												formState={this.props.formState}
												control={this.props.control}
												trigger={this.props.trigger}
												placeholder="Cerca per nome brand..."
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<MatesTextField
												name="address"
												label="Località"
												variant="outlined"
												formState={this.props.formState}
												control={this.props.control}
												trigger={this.props.trigger}
												placeholder="Cerca per località..."
											/>
										</Grid>
									</Grid>
								</Box>
							</Grid>

							{/* Sezione Filtri di Stato e Priorità */}
							<Grid item xs={12}>
								<Box sx={{ mb: 2 }}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={4}>
											<MatesSelect
												name="priority"
												label="Priorità"
												variant="outlined"
												formState={this.props.formState}
												control={this.props.control}
												trigger={this.props.trigger}>
												<option value="">Tutte le priorità</option>
												{Object.keys(PriorityLevel).map((priority, index) => {
													const priorityTranslations = new PriorityLevelTranslations();
													return (
														<option value={priority} key={index}>
															{priorityTranslations[priority as keyof PriorityLevelTranslations]}
														</option>
													);
												})}
											</MatesSelect>
										</Grid>
										<Grid item xs={12} md={4}>
											<MatesSelect
												name="status"
												label="Stato"
												variant="outlined"
												formState={this.props.formState}
												control={this.props.control}
												trigger={this.props.trigger}>
												<option value="">Tutti gli stati</option>
												{Object.keys(AssetMatchesBrandStatus).map((status, index) => {
													const statusTranslations = new AssetMatchesBrandStatusTrans();
													return (
														<option value={status} key={index}>
															{statusTranslations[status as keyof AssetMatchesBrandStatusTrans]}
														</option>
													);
												})}
											</MatesSelect>
										</Grid>
										<Grid item xs={12} md={4}>
											<MatesSelect
												name="compatibility"
												label="Compatibilità"
												variant="outlined"
												formState={this.props.formState}
												control={this.props.control}
												trigger={this.props.trigger}>
												<option value="">Tutte le compatibilità</option>
												{["HIGH", "MEDIUM", "TO_CHECK", "LOW"].map((value, index) => {
													const compatibilityTranslations = new CompatibilityTranslations();
													return (
														<option value={value} key={index}>
															{compatibilityTranslations[value as keyof CompatibilityTranslations]}
														</option>
													);
												})}
											</MatesSelect>
										</Grid>
									</Grid>
								</Box>
							</Grid>

							{/* Sezione Filtri Aggiuntivi */}
							<Grid item xs={12}>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										{ /* TODO GESTIRE RICERCA TRAMITE OPERATORE - MANCANTE NEL BE, IN ATTESA DI SVILUPPI */ }
										<MatesSelect
											disabled
											name="operator"
											label="Operatore"
											variant="outlined"
											formState={this.props.formState}
											control={this.props.control}
											trigger={this.props.trigger}>
											<option value="">Seleziona operatore</option>
										</MatesSelect>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</AdvancedSearchbar>
				}
			</Box>

		)
	}
}

const MatchProposalSearchbarWithForm = withUseFormHook<MatchProposalAdvancedSearchForm>(
	MatchProposalAdvancedSearch,
	MatchProposalAdvancedSearchValidation,
	new MatchProposalAdvancedSearchForm()
)

export default withUseRouter(MatchProposalSearchbarWithForm)
